import {
  alpha,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogProps,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  Close,
  Psychology,
  RadioButtonUnchecked,
  RemoveCircleOutline,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { getIndicators } from "apis";
import { useTranslation } from "react-i18next";

interface ILocalizedRow {
  id: number;
  code: string;
  labelEn: string;
  labelFr?: string;
  labelAr?: string;
}

interface ICell {
  area?: ILocalizedRow;
  ageBand?: ILocalizedRow;
}

const pickLabel = (row: ILocalizedRow | undefined, lang: string): string => {
  if (!row) return "";
  const short = lang.substring(0, 2).toLowerCase();
  return (
    (short === "fr" ? row.labelFr : short === "ar" ? row.labelAr : null) ??
    row.labelEn
  );
};

interface FrameworkIndicatorDialogProps extends DialogProps {
  onClose: () => void;
  event: { id: string };
}

// Backend status codes: 0 = not assessed, 1 = working towards, 2 = achieved.
const STATUS_ACHIEVED = 2;
const STATUS_WORKING = 1;

const statusIcon = (status: number) => {
  switch (status) {
    case STATUS_ACHIEVED:
      return <CheckCircle fontSize="small" color="success" />;
    case STATUS_WORKING:
      return <RemoveCircleOutline fontSize="small" color="warning" />;
    default:
      return <RemoveCircleOutline fontSize="small" color="disabled" />;
  }
};

const FrameworkIndicatorDialog: React.FC<FrameworkIndicatorDialogProps> = ({
  open,
  onClose,
  event,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [indicators, setIndicators] = useState<any[]>([]);
  const [cell, setCell] = useState<ICell>({});

  useEffect(() => {
    getIndicators(event.id).then((data) => {
      // Tolerate legacy object-shaped indicators ({ "<matterId>": ind })
      // alongside the canonical array shape.
      const raw = data.indicators;
      const list = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object"
          ? Object.values(raw)
          : [];
      setIndicators(list);
      setCell({ area: data.area, ageBand: data.ageBand });
    });
  }, [event.id, i18n.language]);

  const achieved = indicators.filter(
    (i: any) => i.status === STATUS_ACHIEVED,
  ).length;
  const emerging = indicators.filter(
    (i: any) => i.status === STATUS_WORKING,
  ).length;
  const total = indicators.length;
  const pct = total > 0 ? (achieved / total) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        position: "absolute",
        height: "100%",
        width: 1 / 3,
        right: 0,
        left: "auto",
        [theme.breakpoints.down("md")]: { width: 1 / 2 },
        [theme.breakpoints.down("sm")]: { width: "100%" },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          height: "100%",
          margin: 0,
          maxHeight: "none",
          maxWidth: "none",
          borderRadius: 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Psychology />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              {t("labels.framework_indicators")}
            </Typography>
            {(cell.area || cell.ageBand) && (
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {pickLabel(cell.area, i18n.language)}
                {cell.area && cell.ageBand ? " · " : ""}
                {pickLabel(cell.ageBand, i18n.language)}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Summary bar */}
        <Box sx={{ px: 2.5, py: 2, bgcolor: "background.paper" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {t("grading.objective_count", { count: total })}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75 }}>
              {achieved > 0 && (
                <Chip
                  icon={<CheckCircle sx={{ fontSize: 14 }} />}
                  label={achieved}
                  size="small"
                  color="success"
                  sx={{ height: 22, fontSize: 12, fontWeight: 700 }}
                />
              )}
              {emerging > 0 && (
                <Chip
                  icon={<RemoveCircleOutline sx={{ fontSize: 14 }} />}
                  label={emerging}
                  size="small"
                  color="warning"
                  sx={{ height: 22, fontSize: 12, fontWeight: 700 }}
                />
              )}
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.success.main, 0.12),
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                bgcolor: "success.main",
              },
            }}
          />
        </Box>

        <Divider />

        {/* Indicators list */}
        <List disablePadding>
          {indicators.map(({ description, status }: any, index: number) => (
            <ListItem
              key={index}
              sx={{
                px: 2.5,
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
                gap: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {statusIcon(status)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    {description}
                  </Typography>
                }
              />
              <Chip
                label={
                  status === STATUS_ACHIEVED
                    ? t("grading.achieved")
                    : status === STATUS_WORKING
                      ? t("grading.working_towards")
                      : t("grading.not_observed")
                }
                color={
                  status === STATUS_ACHIEVED
                    ? "success"
                    : status === STATUS_WORKING
                      ? "warning"
                      : "default"
                }
                variant="outlined"
                sx={{
                  minWidth: 130,
                  height: 28,
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default FrameworkIndicatorDialog;
